import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { FormattedMessage } from 'react-intl';

import {
  Label,
  TextField,
  Tooltip,
  Select,
} from '@folio/stripes/components';

import { FIXED_FIELD_MAX_LENGTH } from '../../../common/constants';

import styles from './BytesField.css';

export const SUBFIELD_TYPES = {
  BYTE: 'Byte',
  STRING: 'String',
  BYTES: 'Bytes',
  SELECT: 'Select',
};

const renderSubField = (name, config) => {
  const fieldName = `${name}.${config.name}`;
  const label = <FormattedMessage id={`ui-quick-marc.record.fixedField.${config.name}`} />;
  const hint = config.hint ? config.hint : config.name;
  const labelHint = <FormattedMessage id={`ui-quick-marc.record.fixedField.tip.${hint}`} />;
  const defaultValue = new Array(config.length || 1).fill('\\').join('');

  if (config.type === SUBFIELD_TYPES.BYTES) {
    return (
      <FieldArray name={fieldName}>
        {() => {
          return (
            <div className={styles.bytesFieldSubFieldBytes}>
              <Label htmlFor={`${fieldName}[0]`}>
                {label}
              </Label>

              <div className={styles.bytesFieldSubFieldBytesList}>
                {
                  Array.from(Array(config.bytes)).map((v, idx) => {
                    return (
                      <FormattedMessage
                        key={idx}
                        id={`ui-quick-marc.record.fixedField.${config.name}`}
                      >
                        {([ariaLabel]) => (

                          <Tooltip
                            id={`ui-quick-marc.record.fixedField-${config.name}${idx}`}
                            text={labelHint}
                            placement="bottom-start"
                          >
                            {({ ref, ariaIds }) => (
                              <Field
                                inputRef={ref}
                                dirty={false}
                                ariaLabel={ariaLabel}
                                aria-labelledby={ariaIds.text}
                                name={`${fieldName}[${idx}]`}
                                component={TextField}
                                maxLength={FIXED_FIELD_MAX_LENGTH}
                                disabled={config.disabled}
                                className={styles.fixedFieldSubFieldByte}
                                hasClearIcon={false}
                                data-testid={`fixed-field-${config.type}`}
                                parse={value => value || defaultValue}
                                defaultValue={defaultValue}
                              />
                            )}
                          </Tooltip>
                        )}
                      </FormattedMessage>
                    );
                  })
                }
              </div>
            </div>
          );
        }}
      </FieldArray>
    );
  }

  if (config.type === SUBFIELD_TYPES.SELECT) {
    return (
      <div className={styles.bytesFieldSubFieldSelect}>
        <Field
          dirty={false}
          name={fieldName}
          label={label}
          component={Select}
          disabled={config.disabled}
          dataOptions={config.options}
          data-testid={`fixed-field-${config.type}`}
        />
      </div>
    );
  }

  const getMaxLengthByType = config.type === SUBFIELD_TYPES.BYTE ? FIXED_FIELD_MAX_LENGTH : config.length;

  return (
    <FormattedMessage id={`ui-quick-marc.record.fixedField.${config.name}`}>
      {([ariaLabel]) => (
        <Tooltip
          id={`ui-quick-marc.record.fixedField-${config.name}`}
          text={labelHint}
          placement="bottom-start"
        >
          {({ ref, ariaIds }) => (
            <Field
              inputRef={ref}
              dirty={false}
              ariaLabel={ariaLabel}
              aria-labelledby={ariaIds.text}
              name={fieldName}
              label={label}
              component={TextField}
              disabled={config.disabled}
              maxLength={getMaxLengthByType}
              className={styles[`bytesFieldSubField${config.type}`]}
              hasClearIcon={false}
              data-testid={`fixed-field-${config.type}`}
              defaultValue={defaultValue}
            />
          )}
        </Tooltip>
      )}
    </FormattedMessage>
  );
};

export const BytesField = ({ config, name, id }) => {
  return (
    <div
      className={styles.bytesFieldRow}
      data-testid={id || `row-${name}`}
    >
      {
        config.fields.map((field, fieldIdx) => {
          return (
            <div
              key={fieldIdx}
              data-testid="bytes-field-col"
            >
              {renderSubField(name, field)}
            </div>
          );
        })
      }
    </div>
  );
};

BytesField.propTypes = {
  name: PropTypes.string.isRequired,
  config: PropTypes.object.isRequired,
  id: PropTypes.string,
};

BytesField.defaultProps = {
  id: '',
};
